/**
 * Nautico Premium — Theme constants for Admin Dashboard.
 * Dark luxury nautical style: yacht club, deep sea, gold accents.
 */

// ─── Colors ──────────────────────────────────────────────────────────────
export const NP = {
  // Backgrounds
  bgRadial: 'bg-[radial-gradient(ellipse_at_top,#1B3254,#112240_60%,#0D1B30)]',
  bgCard: 'bg-[linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))]',
  bgCardHover: 'hover:bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))]',

  // Card styles
  card: 'bg-[linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] backdrop-blur-xl border border-[rgba(96,165,250,0.15)] rounded-[18px] shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-250 ease-out',
  cardHover: 'hover:translate-y-[-4px] hover:shadow-[0_16px_48px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)]',

  // Text
  textPrimary: 'text-[#F4F6F8]',
  textMuted: 'text-[#9BA7B4]',
  textGold: 'text-[#60A5FA]',
  textSoftGold: 'text-[#93C5FD]',

  // Accent colors
  gold: '#60A5FA',
  softGold: '#93C5FD',
  success: '#3BCB7E',
  warning: '#E8A23A',
  danger: '#E05252',
  info: '#4DA6FF',

  // KPI number style
  kpiValue: 'text-[34px] font-semibold tracking-tight text-[#F4F6F8]',

  // Trend badges
  trendUp: 'bg-[rgba(59,203,126,0.15)] text-[#3BCB7E]',
  trendDown: 'bg-[rgba(224,82,82,0.15)] text-[#E05252]',

  // Table
  tableRowHover: 'hover:bg-[rgba(96,165,250,0.06)]',
  tableHeader: 'text-[#9BA7B4] text-xs font-semibold uppercase tracking-wider border-b border-[rgba(255,255,255,0.06)]',
  tableCell: 'border-b border-[rgba(255,255,255,0.04)]',

  // Badge variants
  badgeActive: 'bg-[rgba(59,203,126,0.15)] text-[#3BCB7E] border border-[rgba(59,203,126,0.2)]',
  badgePending: 'bg-[rgba(232,162,58,0.15)] text-[#E8A23A] border border-[rgba(232,162,58,0.2)]',
  badgeDanger: 'bg-[rgba(224,82,82,0.15)] text-[#E05252] border border-[rgba(224,82,82,0.2)]',
  badgeInfo: 'bg-[rgba(77,166,255,0.15)] text-[#4DA6FF] border border-[rgba(77,166,255,0.2)]',
  badgeNeutral: 'bg-[rgba(255,255,255,0.06)] text-[#9BA7B4] border border-[rgba(255,255,255,0.08)]',

  // Buttons
  btnGold: 'bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-[#FFFFFF] font-bold rounded-xl transition-all duration-250 hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(96,165,250,0.3)]',
  btnGhost: 'text-[#9BA7B4] hover:text-[#F4F6F8] hover:bg-[rgba(255,255,255,0.06)] rounded-xl transition-all duration-250',
  btnDanger: 'bg-[rgba(224,82,82,0.15)] text-[#E05252] hover:bg-[rgba(224,82,82,0.25)] rounded-xl transition-all duration-250',

  // Inputs
  input: 'bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded-xl text-[#F4F6F8] placeholder-[#6B7A8A] focus:border-[#60A5FA] focus:ring-1 focus:ring-[#60A5FA] outline-none transition-all duration-250',
  select: 'bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded-xl text-[#F4F6F8] focus:border-[#60A5FA] outline-none transition-all duration-250',

  // Tabs
  tabActive: 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA] border-b-2 border-[#60A5FA]',
  tabInactive: 'text-[#6B7A8A] border-b-2 border-transparent hover:text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.03)]',

  // Filters
  filterActive: 'bg-[rgba(96,165,250,0.15)] text-[#60A5FA] border border-[rgba(96,165,250,0.3)]',
  filterInactive: 'bg-[rgba(255,255,255,0.04)] text-[#9BA7B4] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(96,165,250,0.2)] hover:text-[#93C5FD]',

  // Modal
  modalOverlay: 'bg-[rgba(8,16,26,0.8)] backdrop-blur-sm',
  modalCard: 'bg-[linear-gradient(135deg,#1B3254,#132843)] border border-[rgba(96,165,250,0.2)] rounded-[24px] shadow-[0_24px_64px_rgba(0,0,0,0.5)]',

  // Divider
  divider: 'border-[rgba(255,255,255,0.06)]',

  // Scrollbar (for inline usage)
  scrollbar: '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgba(96,165,250,0.2)] [&::-webkit-scrollbar-thumb]:rounded-full',

  // Accent light backgrounds (for info boxes, highlights)
  accentInfoBg: 'bg-[rgba(77,166,255,0.12)]',
  accentInfoBorder: 'border-[rgba(77,166,255,0.25)]',
  accentInfoText: 'text-[#4DA6FF]',
  accentWarningBg: 'bg-[rgba(232,162,58,0.12)]',
  accentWarningBorder: 'border-[rgba(232,162,58,0.25)]',
  accentWarningText: 'text-[#E8A23A]',
  accentSuccessBg: 'bg-[rgba(59,203,126,0.12)]',
  accentSuccessBorder: 'border-[rgba(59,203,126,0.25)]',
  accentSuccessText: 'text-[#3BCB7E]',
  accentDangerBg: 'bg-[rgba(224,82,82,0.12)]',
  accentDangerBorder: 'border-[rgba(224,82,82,0.25)]',
  accentDangerText: 'text-[#E05252]',
  bgSubtle: 'bg-[rgba(255,255,255,0.03)]',
  bgMuted: 'bg-[rgba(255,255,255,0.06)]',
  borderSubtle: 'border-[rgba(255,255,255,0.08)]',

  // Chart colors
  chartGold: '#60A5FA',
  chartBlue: '#4DA6FF',
} as const;

// ─── Helper: owner filter buttons with Nautico Premium styling ──────────
export const npFilterClass = (isActive: boolean) =>
  `flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-250 ${
    isActive ? NP.filterActive : NP.filterInactive
  }`;

export const npFilterBadge = (isActive: boolean) =>
  `text-xs px-1.5 py-0.5 rounded-full ${
    isActive ? 'bg-[rgba(96,165,250,0.2)]' : 'bg-[rgba(255,255,255,0.06)]'
  }`;

// ─── Helper: status badge ───────────────────────────────────────────────
export const npStatusBadge = (status: string) => {
  switch (status) {
    case 'CONFIRMED':
    case 'COMPLETED':
    case 'APPROVED':
      return NP.badgeActive;
    case 'PENDING':
    case 'PENDING_REVIEW':
      return NP.badgePending;
    case 'CANCELLED':
    case 'REJECTED':
    case 'SUSPENDED':
      return NP.badgeDanger;
    default:
      return NP.badgeNeutral;
  }
};
